import React, { useContext, useState, useEffect } from "react";
import { ThemeContext, PatientContext } from "../lib/main-context";
import { useTranslation } from "../i18n/TranslationProvider";

import { Redirect, useHistory, useLocation } from "react-router-dom";

import theme from "../index.scss";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import SubmitButton from "./StyledComponents/SubmitButton";
import ComponentWrapper from "./DashboardComponents/ComponentWrapper";
import SendMessage from "./DashboardComponents/SendMessage";
import SelectProviderDropdown from "./SelectProviderDropdown";
import VideoPlayer from "./DashboardComponents/VideoPlayer";

import { updateMessageRead, getProviderList } from "../api/messageRequests";


import useWindowSize from "../lib/useWindowSize";

const Messages = ({ user, messages, setMessages }) => {
  const history = useHistory();
  const location = useLocation();
  const [width] = useWindowSize();

  const { t } = useTranslation();
  const { dark } = useContext(ThemeContext);
  const { patient } = useContext(PatientContext);

  const [currentProviderId, setCurrentProviderId] = useState(null);
  const [currentVirtualTherapistId, setCurrentVirtualTherapistId] = useState(null);
  const [currentVirtualTherapistName, setCurrentVirtualTherapistName] = useState(null);
  const [currentProviderName, setCurrentProviderName] = useState(null);
  const [showSendMessage, setShowSendMessage] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  // Messages that had "Mark as Read" clicked but haven't been replied to yet —
  // kept pinned in the unread/top position until Reply is clicked on them.
  const [stillPinnedIds, setStillPinnedIds] = useState(new Set());
  const [providerList, setProviderList] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [lastSentRecipientName, setLastSentRecipientName] = useState("");

  const capitalizeName = (firstName, lastName) =>
    firstName.charAt(0).toUpperCase() +
    firstName.slice(1).toLowerCase() +
    " " +
    lastName.charAt(0).toUpperCase() +
    lastName.slice(1).toLowerCase();

  const formatTimestamp = (ts) => {
    if (!ts) return new Date().toLocaleString();
    const date = new Date(ts);
    if (isNaN(date.getTime())) return ts;
    const weekday = date.toLocaleString("en-US", { weekday: "short" });
    const datePart = date.toLocaleString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
    const timePart = date.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${weekday} ${datePart} ${timePart}`;
  };

  useEffect(() => {
    const fetchProviderList = async () => {
      try {
        const res = await getProviderList();
        const providers = res.data;
        const formattedProviders = providers.map((provider) => ({
          value: provider.provider_id,
          label: provider.full_name,
        }));
        setProviderList(formattedProviders);
      } catch (err) {
        console.error("Error fetching provider list", err);
      }
    };

    fetchProviderList();
  }, []);

  // if no patient context OR messages, redirect user to
  // patientControl to force them to query for this info
  if (!location.state?.curPatient || !messages) {
    return <Redirect to="/patientControl" />;
  }

  const { curPatient } = location.state;

  const setMessageAsRead = ({ messageId }) => {
    updateMessageRead({ messageId })
      .then(() => {
        setMessages((originalMessages) => {
          const messageIndex = originalMessages.findIndex(
            (message) => message.messageId === messageId
          );
          const updatedMessages = [...originalMessages];
          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            hasBeenRead: true,
          };
          return updatedMessages;
        });
      })
      .catch((err) => {
        console.error("Failed to mark message as read:", err);
        // Non-fatal — do not redirect to /oops
      });
  };

  // "Mark as Read" on an unread message: marks it read, but keeps it pinned in
  // the top group until the user actually replies to it (see the sort below,
  // which treats stillPinnedIds the same as an unread message).
  const handleMarkAsRead = (messageId) => {
    setMessageAsRead({ messageId });
    setStillPinnedIds((prev) => new Set(prev).add(messageId));
  };

  // "Reply" is what actually releases a message down to the Read group. If it
  // hasn't been marked read yet (user skipped straight to Reply), mark it read
  // now — replying implies it's been read.
  const handleReplyClick = (row) => {
    if (!row.hasBeenRead) {
      setMessageAsRead({ messageId: row.messageId });
    }
    setStillPinnedIds((prev) => {
      if (!prev.has(row.messageId)) return prev;
      const next = new Set(prev);
      next.delete(row.messageId);
      return next;
    });
    setReplyTo(row);
    setShowSendMessage(true);
  };

  const toggleShowSendMessage = () => {
    setShowSendMessage((prevState) => !prevState);
    setReplyTo(null);
  };

  const onMessageSent = (newMessage, recipientName) => {
    // 1. Close the send modal
    setShowSendMessage(false);
    setReplyTo(null);

    const baseTimestamp = newMessage.timestamp ?? new Date().toISOString();
    const baseBody = newMessage.body ?? newMessage.message ?? "";
    const baseMedia = {
      mediaUid: newMessage.mediaUid ?? null,
      mediaType: newMessage.mediaType ?? null,
    };
    const direction = user.userType === "provider" ? "from" : "to";

    setMessages((prev) => {
      const providerRow = {
        messageId: newMessage.messageId ?? newMessage.id ?? Date.now(),
        direction,
        providerId: newMessage.providerId ?? currentProviderId,
        providerName: newMessage.providerName ?? (
          user.userType === "provider"
            ? capitalizeName(user.firstName, user.lastName)
            : recipientName ?? currentProviderName
        ),
        body: baseBody,
        timestamp: baseTimestamp,
        hasBeenRead: false,
        ...baseMedia,
      };

      // If a VT was also messaged, add a second optimistic row for them
      const vtRow = currentVirtualTherapistId ? {
        messageId: `${Date.now()}-vt`,
        direction,
        providerId: currentVirtualTherapistId,
        providerName: currentVirtualTherapistName,
        body: baseBody,
        timestamp: baseTimestamp,
        hasBeenRead: false,
        ...baseMedia,
      } : null;

      return [
        providerRow,
        ...(vtRow ? [vtRow] : []),
        ...prev,
      ];
    });

    setLastSentRecipientName(
      currentVirtualTherapistId && currentVirtualTherapistName
        ? { providerName: recipientName ?? currentProviderName ?? "", vtName: currentVirtualTherapistName }
        : recipientName ?? currentProviderName ?? ""
    );
    setShowSuccessModal(true);
  };

  const isUnreadIncoming = (m) => {
    if (user.userType === "provider") {
      // For providers, incoming messages are direction "to" and belong to this provider
      return !m.hasBeenRead && m.direction === "to" && m.providerId === user.userId;
    }
    // For patients/caregivers, incoming messages are direction "from" (sent by provider)
    return !m.hasBeenRead && m.direction === "from";
  };

  // For virtual patients, when a provider is filtered, also include all sent messages
  // to any other provider (these will be VTs). This ensures no VT conversation disappears
  // just because they aren't the assigned VT or selected in the VT dropdown.
  const isVirtualPatient = (user.userType === "patient" || user.userType === "caregiver") && patient?.clinic_location === "Virtual";

  // Collect all provider IDs the patient has sent messages to (direction "to")
  const sentToProviderIds = isVirtualPatient
    ? new Set(messages.filter((m) => m.direction === "to").map((m) => m.providerId))
    : new Set();

  const filteredMessages = (currentProviderId
    ? messages.filter((m) =>
        m.providerId === currentProviderId ||
        // For virtual patients, always show all sent VT conversations alongside the selected provider
        (isVirtualPatient && m.direction === "to" && sentToProviderIds.has(m.providerId) && m.providerId !== currentProviderId)
      )
    : messages
  ).slice().sort((a, b) => {
    // Unread incoming messages are pinned to top — as are messages that were
    // marked read via "Mark as Read" but haven't been replied to yet.
    const aPinned = isUnreadIncoming(a) || stillPinnedIds.has(a.messageId);
    const bPinned = isUnreadIncoming(b) || stillPinnedIds.has(b.messageId);
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    // Within each group, newest first (backend sends formatted strings,
    // so fall back to original array order which is already newest-first)
    return 0;
  });

  const columns = [
    {
      dataField: "messageId",
      hidden: "true",
    },
    {
      dataField: "direction",
      text: t("messages.ui.sender"),
      formatter: (direction, row) => {
        if (direction === "to") {
          return curPatient.caregiverName
            ? curPatient.caregiverName
            : capitalizeName(curPatient.firstName, curPatient.lastName);
        } else {
          return row.providerName;
        }
      },
    },
    {
      dataField: "direction",
      text: t("messages.ui.recipient"),
      formatter: (direction, row) => {
        if (direction === "from") {
          return capitalizeName(curPatient.firstName, curPatient.lastName);
        } else {
          return row.providerName;
        }
      },
    },
   {
      dataField: "body",
      text: t("messages.messagesTable.message"),
      headerStyle: { width: "50%" },
      formatter: (body, row) => ( 
        <div
          style={{
            display: "block",
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.5,
            maxHeight: "300px",
            overflowY: "scroll",
            paddingRight: "0.5rem",
          }}
        >
          {body}

          {row.mediaUid && row.mediaType === "video" && (
            <VideoPlayer mediaUid={row.mediaUid} />
          )}

          {row.mediaUid && row.mediaType === "image" && (
            <img
                // this string of numbers is the cloudflare account hash
              src={`https://imagedelivery.net/lC4xuuyQ0BThUGUevhyX4g/${row.mediaUid}/public`}
              alt={t("messages.ui.attachedImage")}
              style={{ width: "100%", marginTop: 8, borderRadius: 6, cursor: "pointer" }}
              onClick={(e) => window.open(e.target.src, "_blank")}
            />
          )}
        </div>
      ),
    },
    {
      dataField: "timestamp",
      text: t("messages.messagesTable.timestamp"),
      formatter: (timestamp) => formatTimestamp(timestamp),
    },
    {
      dataField: "hasBeenRead",
      text: t("messages.messagesTable.status.label"),
      formatter: (hasBeenRead, row) => {
        const isProvider = user.userType === "provider";
        const isPatient = user.userType !== "provider";
        const belongsToProvider = row.providerId === user.userId;
        const isFromDirection = row.direction === "from";
        const isToDirection = row.direction === "to";

        const showMarkAsReadButton =
          // Patient: Message is from a provider, and unread
          (!hasBeenRead && isPatient && isFromDirection) ||
          // Provider: Message is to the provider, and unread, with matching providerId
          (!hasBeenRead && isProvider && isToDirection && belongsToProvider);

        if (showMarkAsReadButton) {
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                gap: "0.35rem",
              }}
            >
              <SubmitButton
                centered
                onClick={() => {
                  handleMarkAsRead(row.messageId);
                }}
              >
                {t("messages.messagesTable.status.markAsRead")}
              </SubmitButton>
              <SubmitButton
                centered
                onClick={() => {
                  handleReplyClick(row);
                }}
              >
                {t("messages.ui.reply")}
              </SubmitButton>
            </div>
          );
        }

        const statusNode = (
          <span>
            {
              t(`messages.messagesTable.status.${hasBeenRead ? "read" : "delivered"}`)
            }
          </span>
        );

        const sentByCurrentUser =
          (isPatient && isToDirection) ||
          (isProvider && isFromDirection && belongsToProvider);

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
            }}
          >
            {statusNode}
            {!sentByCurrentUser && (
              <SubmitButton
                onClick={() => {
                  handleReplyClick(row);
                }}
              >
                {t("messages.ui.reply")}
              </SubmitButton>
            )}
          </div>
        );
      },
    },
  ];

  const rowStyle = (row) => {
    const style = {};
    style.overflowWrap = "break-word";

    const isProvider = user.userType === "provider";
    const isPatient = user.userType !== "provider";
    const belongsToProvider = row.providerId === user.userId;
    const isFromDirection = row.direction === "from";
    const isToDirection = row.direction === "to";

    const canMarkAsRead =
      // Patient: Message is from a provider, and unread
      (!row.hasBeenRead && isPatient && isFromDirection) ||
      // Provider: Message is to the provider, and unread, with matching providerId
      (!row.hasBeenRead && isProvider && isToDirection && belongsToProvider);

    // Marked as read via the button, but still pinned at top awaiting a reply —
    // a lighter version of the unread highlight so it's visually distinct from
    // both a still-fully-unread row and a row that's actually settled into Read.
    const isPinnedAwaitingReply = row.hasBeenRead && stillPinnedIds.has(row.messageId);

    if (canMarkAsRead) {
      style.backgroundColor = "#a8d8d4";
      style.fontWeight = "600";
      style.borderLeft = "4px solid #3EB1A6";
    } else if (isPinnedAwaitingReply) {
      style.backgroundColor = "#e8f5f3";
      style.borderLeft = "4px solid #3EB1A6";
    }

    return style;
  };

  let messagesDisplay;

  if (width >= 992) {
    messagesDisplay = (
      <BootstrapTable
        className="overflow-hidden"
        keyField="messageId"
        data={filteredMessages || []}
        columns={columns}
        pagination={paginationFactory()}
        rowStyle={rowStyle}
        tableProps={{
          style: { tableLayout: "fixed", width: "100%" },
        }}
        wrapperClasses="table-responsive"
      />
    );
  } else {
    messagesDisplay = filteredMessages.map((message) => {
      const isPinnedAwaitingReply = message.hasBeenRead && stillPinnedIds.has(message.messageId);
      return (
      <Row className="font-weight-normal" key={message.messageId}>
        <Col
          className={`mb-3 border border-dark rounded${
            !message.hasBeenRead ? " font-weight-bold" : ""
          }`}
          style={
            !message.hasBeenRead
              ? { backgroundColor: "#a8d8d4", borderLeft: "4px solid #3EB1A6" }
              : isPinnedAwaitingReply
              ? { backgroundColor: "#e8f5f3", borderLeft: "4px solid #3EB1A6" }
              : {}
          }
        >
          <h4 className="text-center">{message.providerName}</h4>
          <h4 className="text-center">{message.direction}</h4>
          <h4 className="text-center">{formatTimestamp(message.timestamp)}</h4>
          <p>{message.body}</p>
          {message.mediaUid && message.mediaType === "video" && (
            <div>
              <iframe
                src={`https://iframe.videodelivery.net/${message.mediaUid}`}
                style={{ border: "none", width: "100%", height: 200, borderRadius: 6 }}
                allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
              <p className="lead" style={{fontSize: 10}}>
              {t("messages.messagesTable.videoProcessing")}
            </p>
            </div>
          )}

          {message.mediaUid && message.mediaType === "image" && (
          
            <img
            // this string of numbers is the cloudflare account hash
              src={`https://imagedelivery.net/lC4xuuyQ0BThUGUevhyX4g/${message.mediaUid}/public`}
              alt={t("messages.ui.attachedImage")}
              style={{ width: "100%", marginTop: 8, borderRadius: 6, cursor: "pointer" }}
              onClick={(e) => window.open(e.target.src, "_blank")}
            />
          )}
          {(() => {
            const isPatient = user.userType !== "provider";
            const isProvider = user.userType === "provider";
            const belongsToProvider = message.providerId === user.userId;

            const showMarkAsRead =
              (!message.hasBeenRead && isPatient && message.direction === "from") ||
              (!message.hasBeenRead && isProvider && message.direction === "to" && belongsToProvider);

            const sentByCurrentUser =
              (isPatient && message.direction === "to") ||
              (isProvider && message.direction === "from" && belongsToProvider);

            if (showMarkAsRead) {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.35rem",
                  }}
                >
                  <SubmitButton
                    centered
                    onClick={() => handleMarkAsRead(message.messageId)}
                  >
                    {t("messages.messagesTable.status.markAsRead")}
                  </SubmitButton>
                  <SubmitButton
                    centered
                    onClick={() => handleReplyClick(message)}
                  >
                    {t("messages.ui.reply")}
                  </SubmitButton>
                </div>
              );
            }

            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <p className="text-center">
                  {t(`messages.messagesTable.status.${message.hasBeenRead ? "read" : "delivered"}`)}
                </p>
                {!sentByCurrentUser && (
                  <SubmitButton centered onClick={() => handleReplyClick(message)}>
                    {t("messages.ui.reply")}
                  </SubmitButton>
                )}
              </div>
            );
          })()}
        </Col>
      </Row>
      );
    });
  }

  const handleDropdownChange = (selectedOption) => {
    setCurrentProviderId(selectedOption ? selectedOption.value : null);
    setCurrentProviderName(selectedOption ? selectedOption.label : null);
  };

  const handleVirtualTherapistDropdownChange = (selectedOption) => {
    setCurrentVirtualTherapistId(selectedOption ? selectedOption.value : null);
    setCurrentVirtualTherapistName(selectedOption ? selectedOption.label : null);
  };

  // to be used to highlight the unique provider ids with unread messages for the patient
  const uniqueUnreadUserIds = [
    ...new Set(
      messages
        ?.filter(
          (message) => !message.hasBeenRead && message.direction === "from"
        )
        .map((message) => message.providerId)
    ),
  ];

  return (
    <ComponentWrapper style={{ padding: "60px" }}>
      <h1 className="text-center mb-3">
        {user.userType === "provider" ? (
          <>
            {t("messages.ui.headerFor")}
            <br />
            {`${curPatient.firstName} ${curPatient.lastName}`}
          </>
        ) : (
          <>
            {t("messages.messagesHeader.header")}
            <br />
            <span style={{ fontSize: "1.2rem", fontWeight: "normal" }}>
              {t("messages.messagesHeader.yourAssignedProviderIs")}
              {patient?.assignedProvider?.firstName && patient?.assignedProvider?.lastName ? (
                <span style={{ fontSize: "1.8rem" }}>
                  {`${patient.assignedProvider.firstName} ${patient.assignedProvider.lastName}`}
                </span>
              ) : (
                <span style={{ color: "#FDB913", fontWeight: "500", fontSize: "1.8rem" }}>
                  {t("messages.ui.notAssigned")}
                </span>
              )}
            </span>
            {(user.userType === "patient" || user.userType === "caregiver") && patient?.clinic_location === "Virtual" && (
              <>
                <br />
                <span style={{ fontSize: "1.2rem", fontWeight: "normal" }}>
                  {t("messages.messagesHeader.yourVirtualTherapistIs")}
                  {patient?.assignedVirtualTherapist?.firstName && patient?.assignedVirtualTherapist?.lastName ? (
                    <span style={{ fontSize: "1.8rem" }}>
                      {`${patient.assignedVirtualTherapist.firstName} ${patient.assignedVirtualTherapist.lastName}`}
                    </span>
                  ) : (
                    <span style={{ color: "#FDB913", fontWeight: "500", fontSize: "1.8rem" }}>
                      {t("messages.ui.notAssigned")}
                    </span>
                  )}
                </span>
              </>
            )}
          </>
        )}
      </h1>
      <>
        {/* ── Send-message modal ── */}
        <Modal
        size="lg"
          className="align-items-center"
          show={showSendMessage}
          onHide={toggleShowSendMessage}
        >
          <Modal.Header closeButton>
            <Modal.Title> </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SendMessage
              recipientId={
                user.userType !== "provider"
                  ? replyTo
                    ? replyTo.providerId
                    : currentProviderId
                  : curPatient.patientId
              }
              recipientName={
                user.userType !== "provider"
                  ? replyTo
                    ? replyTo.providerName
                    : currentProviderName
                  : capitalizeName(curPatient.firstName, curPatient.lastName)
              }
              virtualTherapistName={
                (user.userType === "patient" || user.userType === "caregiver") &&
                patient?.clinic_location === "Virtual" &&
                !replyTo
                  ? currentVirtualTherapistName
                  : null
              }
              virtualTherapistId={
                (user.userType === "patient" || user.userType === "caregiver") &&
                patient?.clinic_location === "Virtual" &&
                !replyTo
                  ? currentVirtualTherapistId
                  : null
              }
              userType={user.userType}
              replyTo={replyTo}
              onMessageSent={onMessageSent}
              onMediaCanceled={() => setShowCancelModal(true)} 
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={toggleShowSendMessage}>
              {t("messages.close")}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ── Success confirmation modal ── */}
        <Modal
          className="d-flex align-items-center"
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("messages.ui.messageSent")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ marginBottom: 0 }}>
              {typeof lastSentRecipientName === "object"
                ? `${t("messages.sendMessages.messageSentWithVt")} ${lastSentRecipientName.vtName} ${t("messages.sendMessages.virtualTherapistLabel")} ${t("messages.sendMessages.and")} ${lastSentRecipientName.providerName} ${t("messages.sendMessages.providerLabel")}.`
                : t("messages.messagesTable.status.delivered")
                  ? `${t("messages.sendMessages.messageSent")}${lastSentRecipientName}.`
                  : t("messages.ui.messageSentFallback", { name: lastSentRecipientName })}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowSuccessModal(false)}>
              {t("messages.close")}
            </Button>
          </Modal.Footer>
        </Modal>


        {/* ── Cancel media upload modal ── */}
        <Modal
          className="d-flex align-items-center"
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("messages.messagesHeader.messageCanceledTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ marginBottom: 0 }}>
            {t("messages.messagesHeader.messageCanceledMsg")}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowCancelModal(false)}>
              {t("messages.close")}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
      <Row>
        <Col className="align-items-end">
          <div className="mb-4">
            <h6>{t("messages.messagesHeader.selectProvider")}</h6>
            <SelectProviderDropdown
              options={providerList}
              onChange={handleDropdownChange}
              selectedProviderId={currentProviderId}
              uniqueUnreadUserIds={uniqueUnreadUserIds}
              userType={user.userType}
            />
          </div>
          {(user.userType === "patient" || user.userType === "caregiver") && patient?.clinic_location === "Virtual" && (
            <div className="mb-4">
              <h6>{t("messages.messagesHeader.selectVirtualTherapist")}</h6>
              <SelectProviderDropdown
                options={providerList}
                onChange={handleVirtualTherapistDropdownChange}
                selectedProviderId={currentVirtualTherapistId}
                uniqueUnreadUserIds={uniqueUnreadUserIds}
                userType={user.userType}
              />
            </div>
          )}
        </Col>
      </Row>
      <Row>
        <Col
          className={`d-flex flex-wrap align-items-end ${
            width >= 992 ? "justify-content-start" : "justify-content-center"
          }`}
        >
          <h6
            className="pt-2 pb-2 mb-4"
            style={{
              width: "90%",
              borderRadius: "20px",
              backgroundColor: dark ? theme.darkModePrimary : "#3EB1A6",
              color: dark ? theme.darkModeText : "white",
              textAlign: "center",
              overflowWrap: "break-word",
              cursor: "pointer",
            }}
            onClick={() => history.push("/")}
          >
            {t("messages.messagesHeader.dashboard")}
          </h6>
        </Col>
        <Col
          className={`d-flex flex-wrap align-items-end ${
            width >= 992 ? "justify-content-start" : "justify-content-center"
          }`}
        >
          <h6
            className="pt-2 pb-2 mb-4"
            style={{
              width: "90%",
              borderRadius: "20px",
              backgroundColor: dark ? theme.darkModePrimary : "#3EB1A6",
              color: dark ? theme.darkModeText : "white",
              textAlign: "center",
              overflowWrap: "break-word",
              cursor: "pointer",
            }}
            onClick={toggleShowSendMessage}
          >
            {t("messages.messagesHeader.sendMessage")}
          </h6>
        </Col>
        <Col
          className={`d-flex flex-wrap align-items-end ${
            width >= 992 ? "justify-content-start" : "justify-content-center"
          }`}
        >
          <h6
            className="pt-2 pb-2 mb-4"
            style={{
              width: "90%",
              borderRadius: "20px",
              backgroundColor: dark ? theme.darkModePrimary : "#3EB1A6",
              color: dark ? theme.darkModeText : "white",
              textAlign: "center",
              overflowWrap: "break-word",
              cursor: "pointer",
            }}
            onClick={() => {
              setCurrentProviderId(null);
              setCurrentProviderName(null);
            }}
          >
            {t("messages.messagesHeader.viewAllMessages")}
          </h6>
        </Col>
      </Row>
      {messagesDisplay}
    </ComponentWrapper>
  );
};

export default Messages;